import { useCallback, useEffect, useRef, useState } from "react";

import { Trans, useLingui } from "@lingui/react/macro";
import { apiFetch } from "@pontimed/api-client";
import { cn } from "@pontimed/ui";
import { AnimatePresence, m } from "framer-motion";
import {
  Bell,
  Calendar,
  FileText,
  ImageIcon,
  Loader2,
  Paperclip,
  Plus,
  Sparkles,
  Tag,
  Trash2,
  Upload,
  Users,
  X,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ResponsiveSheet } from "@/components/ui/responsive-sheet";
import { showToast } from "@/lib/ui/toast";
import { handleVaultLocked, isVaultLockedResponse, reportVaultLocked } from "@/lib/vault-unlock";

/* --- Public types --- */

// Shape used by chart components in apps/app (health-metrics-chart,
// lab-trends-chart). Keep verbatim — TODO dedupe to a shared module.
export interface JournalEntryData {
  id: string;
  type: string;
  title: string;
  notes: string | null;
  eventDate: string;
  tags: string[];
  status: string;
  isPinned: boolean;
  metadata: Record<string, unknown> | null;
  providerId: string | null;
  createdAt: string;
  _count: { attachments: number; reminders: number };
  reminders: { id: string; remindAt: string; message: string; status: string }[];
}

export interface VaultMemberOption {
  id: string;
  name: string;
  relationship: string;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entryType: string;
  editingEntry: JournalEntryData | null;
  onSaved: () => void;
  isPriority?: boolean;
  planTier?: string;
  activeMemberId?: string | null;
  vaultMembers?: VaultMemberOption[];
}

/* --- Constants --- */

const ACCEPTED_FILE_TYPES = ".pdf,.jpg,.jpeg,.png,.webp,.dcm";
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const ALL_ENTRY_TYPES = [
  "note",
  "document",
  "lab_result",
  "imaging",
  "prescription",
  "vaccine",
  "metric",
  "visit",
  "reminder",
] as const;

type EntryType = (typeof ALL_ENTRY_TYPES)[number];

// Reminder suggestion config per entry type. textKey resolved via switch
// in render so Lingui can extract literal messages.
const REMINDER_SUGGESTION_TYPES = ["lab_result", "prescription", "vaccine", "visit"] as const;
function isSuggestionType(t: string): t is (typeof REMINDER_SUGGESTION_TYPES)[number] {
  return (REMINDER_SUGGESTION_TYPES as readonly string[]).includes(t);
}

function addMonths(date: Date, months: number): Date {
  const d = new Date(date);
  d.setMonth(d.getMonth() + months);
  return d;
}

/* --- Type-specific metadata types --- */

interface LabValue {
  name: string;
  value: string;
  unit: string;
  refMin: string;
  refMax: string;
}

interface Medication {
  name: string;
  dosage: string;
  frequency: string;
  startDate: string;
  endDate: string;
}

interface VaccineData {
  vaccineName: string;
  manufacturer: string;
  lotNumber: string;
  doseNumber: string;
  totalDoses: string;
  injectionSite: string;
}

type MetricType =
  | "blood_pressure"
  | "glucose"
  | "weight"
  | "temperature"
  | "heart_rate"
  | "oxygen_saturation";

interface MetricData {
  metricType: MetricType | "";
  systolic: string;
  diastolic: string;
  pulse: string;
  glucoseValue: string;
  glucoseContext: string;
  weightValue: string;
  temperatureValue: string;
  heartRateValue: string;
  oxygenValue: string;
  contextNote: string;
}

const INJECTION_SITES = ["left_arm", "right_arm", "left_thigh", "right_thigh"] as const;
const METRIC_TYPES: MetricType[] = [
  "blood_pressure",
  "glucose",
  "weight",
  "temperature",
  "heart_rate",
  "oxygen_saturation",
];
const GLUCOSE_CONTEXTS = ["fasting", "after_meal", "random"] as const;

/* --- Build metadata from type-specific state --- */

function buildMetadata(
  entryType: string,
  labName: string,
  labValues: LabValue[],
  medications: Medication[],
  prescriberName: string,
  vaccineData: VaccineData,
  metricData: MetricData,
): Record<string, unknown> | null {
  switch (entryType) {
    case "lab_result": {
      const vals = labValues.filter((v) => v.name.trim());
      if (!vals.length && !labName.trim()) return null;
      return {
        labName: labName.trim() || undefined,
        values: vals.map((v) => ({
          name: v.name.trim(),
          value: v.value ? parseFloat(v.value) : null,
          unit: v.unit.trim() || undefined,
          refMin: v.refMin ? parseFloat(v.refMin) : null,
          refMax: v.refMax ? parseFloat(v.refMax) : null,
        })),
      };
    }
    case "prescription": {
      const meds = medications.filter((m) => m.name.trim());
      if (!meds.length && !prescriberName.trim()) return null;
      return {
        prescriberName: prescriberName.trim() || undefined,
        medications: meds.map((m) => ({
          name: m.name.trim(),
          dosage: m.dosage.trim() || undefined,
          frequency: m.frequency.trim() || undefined,
          startDate: m.startDate || undefined,
          endDate: m.endDate || undefined,
        })),
      };
    }
    case "vaccine": {
      const has = Object.values(vaccineData).some((v) => v.trim());
      if (!has) return null;
      return {
        vaccineName: vaccineData.vaccineName.trim() || undefined,
        manufacturer: vaccineData.manufacturer.trim() || undefined,
        lotNumber: vaccineData.lotNumber.trim() || undefined,
        doseNumber: vaccineData.doseNumber ? parseInt(vaccineData.doseNumber) : undefined,
        totalDoses: vaccineData.totalDoses ? parseInt(vaccineData.totalDoses) : undefined,
        injectionSite: vaccineData.injectionSite || undefined,
      };
    }
    case "metric": {
      if (!metricData.metricType) return null;
      const base: Record<string, unknown> = {
        metricType: metricData.metricType,
        contextNote: metricData.contextNote.trim() || undefined,
      };
      switch (metricData.metricType) {
        case "blood_pressure":
          base.systolic = metricData.systolic ? parseFloat(metricData.systolic) : null;
          base.diastolic = metricData.diastolic ? parseFloat(metricData.diastolic) : null;
          base.pulse = metricData.pulse ? parseFloat(metricData.pulse) : null;
          break;
        case "glucose":
          base.value = metricData.glucoseValue ? parseFloat(metricData.glucoseValue) : null;
          base.unit = "mg/dL";
          base.context = metricData.glucoseContext || undefined;
          break;
        case "weight":
          base.value = metricData.weightValue ? parseFloat(metricData.weightValue) : null;
          base.unit = "kg";
          break;
        case "temperature":
          base.value = metricData.temperatureValue ? parseFloat(metricData.temperatureValue) : null;
          base.unit = "°C";
          break;
        case "heart_rate":
          base.value = metricData.heartRateValue ? parseFloat(metricData.heartRateValue) : null;
          base.unit = "bpm";
          break;
        case "oxygen_saturation":
          base.value = metricData.oxygenValue ? parseFloat(metricData.oxygenValue) : null;
          base.unit = "%";
          break;
      }
      return base;
    }
    default:
      return null;
  }
}

/* --- Parse metadata back into type-specific state --- */

function parseLabValues(meta: Record<string, unknown>): LabValue[] {
  const vals = meta.values;
  if (!Array.isArray(vals)) return [emptyLabValue()];
  return vals.map((v: Record<string, unknown>) => ({
    name: String(v.name || ""),
    value: v.value != null ? String(v.value) : "",
    unit: String(v.unit || ""),
    refMin: v.refMin != null ? String(v.refMin) : "",
    refMax: v.refMax != null ? String(v.refMax) : "",
  }));
}

function parseMedications(meta: Record<string, unknown>): Medication[] {
  const meds = meta.medications;
  if (!Array.isArray(meds)) return [emptyMedication()];
  return meds.map((m: Record<string, unknown>) => ({
    name: String(m.name || ""),
    dosage: String(m.dosage || ""),
    frequency: String(m.frequency || ""),
    startDate: String(m.startDate || ""),
    endDate: String(m.endDate || ""),
  }));
}

function parseVaccine(meta: Record<string, unknown>): VaccineData {
  return {
    vaccineName: String(meta.vaccineName || ""),
    manufacturer: String(meta.manufacturer || ""),
    lotNumber: String(meta.lotNumber || ""),
    doseNumber: meta.doseNumber != null ? String(meta.doseNumber) : "",
    totalDoses: meta.totalDoses != null ? String(meta.totalDoses) : "",
    injectionSite: String(meta.injectionSite || ""),
  };
}

function parseMetric(meta: Record<string, unknown>): MetricData {
  const mt = String(meta.metricType || "");
  return {
    metricType: (METRIC_TYPES.includes(mt as MetricType) ? mt : "") as MetricType | "",
    systolic: meta.systolic != null ? String(meta.systolic) : "",
    diastolic: meta.diastolic != null ? String(meta.diastolic) : "",
    pulse: meta.pulse != null ? String(meta.pulse) : "",
    glucoseValue: meta.metricType === "glucose" && meta.value != null ? String(meta.value) : "",
    glucoseContext: String(meta.context || ""),
    weightValue: meta.metricType === "weight" && meta.value != null ? String(meta.value) : "",
    temperatureValue:
      meta.metricType === "temperature" && meta.value != null ? String(meta.value) : "",
    heartRateValue:
      meta.metricType === "heart_rate" && meta.value != null ? String(meta.value) : "",
    oxygenValue:
      meta.metricType === "oxygen_saturation" && meta.value != null ? String(meta.value) : "",
    contextNote: String(meta.contextNote || ""),
  };
}

/* --- Empty state factories --- */

function emptyLabValue(): LabValue {
  return { name: "", value: "", unit: "", refMin: "", refMax: "" };
}

function emptyMedication(): Medication {
  return { name: "", dosage: "", frequency: "", startDate: "", endDate: "" };
}

function emptyVaccine(): VaccineData {
  return {
    vaccineName: "",
    manufacturer: "",
    lotNumber: "",
    doseNumber: "",
    totalDoses: "",
    injectionSite: "",
  };
}

function emptyMetric(): MetricData {
  return {
    metricType: "",
    systolic: "",
    diastolic: "",
    pulse: "",
    glucoseValue: "",
    glucoseContext: "",
    weightValue: "",
    temperatureValue: "",
    heartRateValue: "",
    oxygenValue: "",
    contextNote: "",
  };
}

/* --- Field label helper --- */

function FieldLabel({ children }: { children: React.ReactNode }) {
  return <label className="text-foreground mb-1 block text-xs font-medium">{children}</label>;
}

/* --- Hooks for translated label maps --- */

function useEntryTypeLabels(): Record<EntryType, string> {
  const { t } = useLingui();
  return {
    note: t`Note`,
    document: t`Document`,
    lab_result: t`Lab result`,
    imaging: t`Imaging`,
    prescription: t`Prescription`,
    vaccine: t`Vaccine`,
    metric: t`Metric`,
    visit: t`Visit`,
    reminder: t`Reminder`,
  };
}

function useTypeTitleLabel(activeType: string, isEditing: boolean): string {
  const { t } = useLingui();
  if (isEditing) return t`Edit entry`;
  switch (activeType) {
    case "note":
      return t`Quick note`;
    case "document":
    case "imaging":
      return t`Add document`;
    case "lab_result":
      return t`Add lab result`;
    case "prescription":
      return t`Add prescription`;
    case "vaccine":
      return t`Add vaccine`;
    case "metric":
      return t`Add metric`;
    case "reminder":
      return t`Add reminder`;
    case "visit":
      return t`Visit`;
    default:
      return t`Add entry`;
  }
}

function useInjectionSiteLabel(): (site: string) => string {
  const { t } = useLingui();
  return (site: string) => {
    switch (site) {
      case "left_arm":
        return t`Left arm`;
      case "right_arm":
        return t`Right arm`;
      case "left_thigh":
        return t`Left thigh`;
      case "right_thigh":
        return t`Right thigh`;
      default:
        return site;
    }
  };
}

function useMetricTypeLabel(): (mt: MetricType) => string {
  const { t } = useLingui();
  return (mt: MetricType) => {
    switch (mt) {
      case "blood_pressure":
        return t`Blood pressure`;
      case "glucose":
        return t`Glucose`;
      case "weight":
        return t`Weight`;
      case "temperature":
        return t`Temperature`;
      case "heart_rate":
        return t`Heart rate`;
      case "oxygen_saturation":
        return t`Oxygen saturation`;
    }
  };
}

function useGlucoseContextLabel(): (ctx: string) => string {
  const { t } = useLingui();
  return (ctx: string) => {
    switch (ctx) {
      case "fasting":
        return t`Fasting`;
      case "after_meal":
        return t`After meal`;
      case "random":
        return t`Random`;
      default:
        return ctx;
    }
  };
}

function useReminderSuggestionText(): (type: string) => string {
  const { t } = useLingui();
  return (type: string) => {
    switch (type) {
      case "lab_result":
        return t`Want a reminder to repeat this lab in 3 months?`;
      case "prescription":
        return t`Want a reminder to renew this prescription?`;
      case "vaccine":
        return t`Want a reminder for the next dose?`;
      case "visit":
        return t`Want a reminder to schedule a follow-up visit?`;
      default:
        return t`Want a reminder for this entry?`;
    }
  };
}

/* --- Type-Specific Fields Component --- */

function TypeSpecificFields({
  entryType,
  labName,
  setLabName,
  labValues,
  setLabValues,
  medications,
  setMedications,
  prescriberName,
  setPrescriberName,
  vaccine,
  setVaccine,
  metric,
  setMetric,
}: {
  entryType: string;
  labName: string;
  setLabName: (v: string) => void;
  labValues: LabValue[];
  setLabValues: (v: LabValue[]) => void;
  medications: Medication[];
  setMedications: (v: Medication[]) => void;
  prescriberName: string;
  setPrescriberName: (v: string) => void;
  vaccine: VaccineData;
  setVaccine: (v: VaccineData) => void;
  metric: MetricData;
  setMetric: (v: MetricData) => void;
}) {
  const { t } = useLingui();
  const injectionSiteLabel = useInjectionSiteLabel();
  const metricTypeLabel = useMetricTypeLabel();
  const glucoseContextLabel = useGlucoseContextLabel();

  const updateLabValue = (idx: number, field: keyof LabValue, val: string) => {
    const next = [...labValues];
    next[idx] = { ...next[idx], [field]: val };
    setLabValues(next);
  };

  const updateMedication = (idx: number, field: keyof Medication, val: string) => {
    const next = [...medications];
    next[idx] = { ...next[idx], [field]: val };
    setMedications(next);
  };

  const updateMetric = (field: keyof MetricData, val: string) => {
    setMetric({ ...metric, [field]: val });
  };

  switch (entryType) {
    case "lab_result":
      return (
        <div className="border-border space-y-3 rounded-lg border p-3">
          <p className="text-foreground text-sm font-medium">
            <Trans>Lab result details</Trans>
          </p>
          <div>
            <FieldLabel>
              <Trans>Lab name</Trans>
            </FieldLabel>
            <Input
              value={labName}
              onChange={(e) => setLabName(e.target.value)}
              placeholder={t`e.g. Synevo, MedLife`}
              className="min-h-[44px]"
            />
          </div>
          <div className="space-y-2">
            <FieldLabel>
              <Trans>Values</Trans>
            </FieldLabel>
            {labValues.map((lv, i) => (
              <div key={i} className="bg-muted/50 flex flex-col gap-2 rounded-md p-2">
                <div className="flex items-center gap-2">
                  <Input
                    value={lv.name}
                    onChange={(e) => updateLabValue(i, "name", e.target.value)}
                    placeholder={t`Name (e.g. Hemoglobin)`}
                    className="min-h-[44px] flex-1"
                  />
                  {labValues.length > 1 && (
                    <button
                      type="button"
                      onClick={() => setLabValues(labValues.filter((_, j) => j !== i))}
                      aria-label={t`Remove value`}
                      className="text-muted-foreground hover:text-destructive flex size-[44px] shrink-0 items-center justify-center rounded-md"
                    >
                      <Trash2 className="size-4" />
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                  <Input
                    type="number"
                    value={lv.value}
                    onChange={(e) => updateLabValue(i, "value", e.target.value)}
                    placeholder={t`Value`}
                    className="min-h-[44px]"
                  />
                  <Input
                    value={lv.unit}
                    onChange={(e) => updateLabValue(i, "unit", e.target.value)}
                    placeholder={t`Unit`}
                    className="min-h-[44px]"
                  />
                  <Input
                    type="number"
                    value={lv.refMin}
                    onChange={(e) => updateLabValue(i, "refMin", e.target.value)}
                    placeholder={t`Ref min`}
                    className="min-h-[44px]"
                  />
                  <Input
                    type="number"
                    value={lv.refMax}
                    onChange={(e) => updateLabValue(i, "refMax", e.target.value)}
                    placeholder={t`Ref max`}
                    className="min-h-[44px]"
                  />
                </div>
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="min-h-[44px] w-full"
              onClick={() => setLabValues([...labValues, emptyLabValue()])}
            >
              <Plus className="mr-1.5 size-4" />
              <Trans>Add value</Trans>
            </Button>
          </div>
        </div>
      );

    case "prescription":
      return (
        <div className="border-border space-y-3 rounded-lg border p-3">
          <p className="text-foreground text-sm font-medium">
            <Trans>Prescription details</Trans>
          </p>
          <div>
            <FieldLabel>
              <Trans>Prescriber name</Trans>
            </FieldLabel>
            <Input
              value={prescriberName}
              onChange={(e) => setPrescriberName(e.target.value)}
              placeholder={t`Provider who prescribed`}
              className="min-h-[44px]"
            />
          </div>
          <div className="space-y-2">
            <FieldLabel>
              <Trans>Medications</Trans>
            </FieldLabel>
            {medications.map((med, i) => (
              <div key={i} className="bg-muted/50 flex flex-col gap-2 rounded-md p-2">
                <div className="flex items-center gap-2">
                  <Input
                    value={med.name}
                    onChange={(e) => updateMedication(i, "name", e.target.value)}
                    placeholder={t`Medication name`}
                    className="min-h-[44px] flex-1"
                  />
                  {medications.length > 1 && (
                    <button
                      type="button"
                      onClick={() => setMedications(medications.filter((_, j) => j !== i))}
                      aria-label={t`Remove medication`}
                      className="text-muted-foreground hover:text-destructive flex size-[44px] shrink-0 items-center justify-center rounded-md"
                    >
                      <Trash2 className="size-4" />
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                  <Input
                    value={med.dosage}
                    onChange={(e) => updateMedication(i, "dosage", e.target.value)}
                    placeholder={t`Dosage (e.g. 500mg)`}
                    className="min-h-[44px]"
                  />
                  <Input
                    value={med.frequency}
                    onChange={(e) => updateMedication(i, "frequency", e.target.value)}
                    placeholder={t`Frequency (e.g. 2x/day)`}
                    className="min-h-[44px]"
                  />
                  <div className="grid grid-cols-2 gap-2">
                    <Input
                      type="date"
                      value={med.startDate}
                      onChange={(e) => updateMedication(i, "startDate", e.target.value)}
                      className="min-h-[44px]"
                      title={t`Start date`}
                    />
                    <Input
                      type="date"
                      value={med.endDate}
                      onChange={(e) => updateMedication(i, "endDate", e.target.value)}
                      className="min-h-[44px]"
                      title={t`End date`}
                    />
                  </div>
                </div>
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="min-h-[44px] w-full"
              onClick={() => setMedications([...medications, emptyMedication()])}
            >
              <Plus className="mr-1.5 size-4" />
              <Trans>Add medication</Trans>
            </Button>
          </div>
        </div>
      );

    case "vaccine":
      return (
        <div className="border-border space-y-3 rounded-lg border p-3">
          <p className="text-foreground text-sm font-medium">
            <Trans>Vaccine details</Trans>
          </p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <FieldLabel>
                <Trans>Vaccine name</Trans>
              </FieldLabel>
              <Input
                value={vaccine.vaccineName}
                onChange={(e) => setVaccine({ ...vaccine, vaccineName: e.target.value })}
                placeholder={t`e.g. Pfizer COVID-19`}
                className="min-h-[44px]"
              />
            </div>
            <div>
              <FieldLabel>
                <Trans>Manufacturer</Trans>
              </FieldLabel>
              <Input
                value={vaccine.manufacturer}
                onChange={(e) => setVaccine({ ...vaccine, manufacturer: e.target.value })}
                placeholder={t`e.g. Pfizer-BioNTech`}
                className="min-h-[44px]"
              />
            </div>
            <div>
              <FieldLabel>
                <Trans>Lot number</Trans>
              </FieldLabel>
              <Input
                value={vaccine.lotNumber}
                onChange={(e) => setVaccine({ ...vaccine, lotNumber: e.target.value })}
                placeholder={t`e.g. EL9262`}
                className="min-h-[44px]"
              />
            </div>
            <div>
              <FieldLabel>
                <Trans>Injection site</Trans>
              </FieldLabel>
              <select
                value={vaccine.injectionSite}
                onChange={(e) => setVaccine({ ...vaccine, injectionSite: e.target.value })}
                className="border-border bg-background text-foreground min-h-[44px] w-full rounded-lg border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-teal-500/20"
              >
                <option value="">{t`Select site…`}</option>
                {INJECTION_SITES.map((site) => (
                  <option key={site} value={site}>
                    {injectionSiteLabel(site)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <FieldLabel>
                <Trans>Dose number</Trans>
              </FieldLabel>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min="1"
                  value={vaccine.doseNumber}
                  onChange={(e) => setVaccine({ ...vaccine, doseNumber: e.target.value })}
                  placeholder="#"
                  className="min-h-[44px] flex-1"
                />
                <span className="text-muted-foreground text-sm">
                  <Trans>of</Trans>
                </span>
                <Input
                  type="number"
                  min="1"
                  value={vaccine.totalDoses}
                  onChange={(e) => setVaccine({ ...vaccine, totalDoses: e.target.value })}
                  placeholder="#"
                  className="min-h-[44px] flex-1"
                />
              </div>
            </div>
          </div>
        </div>
      );

    case "metric":
      return (
        <div className="border-border space-y-3 rounded-lg border p-3">
          <p className="text-foreground text-sm font-medium">
            <Trans>Health metric</Trans>
          </p>
          <div>
            <FieldLabel>
              <Trans>Metric type</Trans>
            </FieldLabel>
            <select
              value={metric.metricType}
              onChange={(e) => {
                const mt = e.target.value as MetricType | "";
                setMetric({ ...emptyMetric(), metricType: mt, contextNote: metric.contextNote });
              }}
              className="border-border bg-background text-foreground min-h-[44px] w-full rounded-lg border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-teal-500/20"
            >
              <option value="">{t`Select type…`}</option>
              {METRIC_TYPES.map((mt) => (
                <option key={mt} value={mt}>
                  {metricTypeLabel(mt)}
                </option>
              ))}
            </select>
          </div>

          {metric.metricType === "blood_pressure" && (
            <div className="grid grid-cols-3 gap-2">
              <div>
                <FieldLabel>
                  <Trans>Systolic (mmHg)</Trans>
                </FieldLabel>
                <Input
                  type="number"
                  value={metric.systolic}
                  onChange={(e) => updateMetric("systolic", e.target.value)}
                  placeholder="120"
                  className="min-h-[44px]"
                />
              </div>
              <div>
                <FieldLabel>
                  <Trans>Diastolic (mmHg)</Trans>
                </FieldLabel>
                <Input
                  type="number"
                  value={metric.diastolic}
                  onChange={(e) => updateMetric("diastolic", e.target.value)}
                  placeholder="80"
                  className="min-h-[44px]"
                />
              </div>
              <div>
                <FieldLabel>
                  <Trans>Pulse (bpm)</Trans>
                </FieldLabel>
                <Input
                  type="number"
                  value={metric.pulse}
                  onChange={(e) => updateMetric("pulse", e.target.value)}
                  placeholder="72"
                  className="min-h-[44px]"
                />
              </div>
            </div>
          )}

          {metric.metricType === "glucose" && (
            <div className="grid grid-cols-2 gap-2">
              <div>
                <FieldLabel>
                  <Trans>Value (mg/dL)</Trans>
                </FieldLabel>
                <Input
                  type="number"
                  value={metric.glucoseValue}
                  onChange={(e) => updateMetric("glucoseValue", e.target.value)}
                  placeholder="100"
                  className="min-h-[44px]"
                />
              </div>
              <div>
                <FieldLabel>
                  <Trans>Context</Trans>
                </FieldLabel>
                <select
                  value={metric.glucoseContext}
                  onChange={(e) => updateMetric("glucoseContext", e.target.value)}
                  className="border-border bg-background text-foreground min-h-[44px] w-full rounded-lg border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-teal-500/20"
                >
                  <option value="">{t`Select…`}</option>
                  {GLUCOSE_CONTEXTS.map((ctx) => (
                    <option key={ctx} value={ctx}>
                      {glucoseContextLabel(ctx)}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {metric.metricType === "weight" && (
            <div>
              <FieldLabel>
                <Trans>Weight (kg)</Trans>
              </FieldLabel>
              <Input
                type="number"
                step="0.1"
                value={metric.weightValue}
                onChange={(e) => updateMetric("weightValue", e.target.value)}
                placeholder="70.5"
                className="min-h-[44px]"
              />
            </div>
          )}

          {metric.metricType === "temperature" && (
            <div>
              <FieldLabel>
                <Trans>Temperature (°C)</Trans>
              </FieldLabel>
              <Input
                type="number"
                step="0.1"
                value={metric.temperatureValue}
                onChange={(e) => updateMetric("temperatureValue", e.target.value)}
                placeholder="36.6"
                className="min-h-[44px]"
              />
            </div>
          )}

          {metric.metricType === "heart_rate" && (
            <div>
              <FieldLabel>
                <Trans>Heart rate (bpm)</Trans>
              </FieldLabel>
              <Input
                type="number"
                value={metric.heartRateValue}
                onChange={(e) => updateMetric("heartRateValue", e.target.value)}
                placeholder="72"
                className="min-h-[44px]"
              />
            </div>
          )}

          {metric.metricType === "oxygen_saturation" && (
            <div>
              <FieldLabel>
                <Trans>SpO2 (%)</Trans>
              </FieldLabel>
              <Input
                type="number"
                value={metric.oxygenValue}
                onChange={(e) => updateMetric("oxygenValue", e.target.value)}
                placeholder="98"
                className="min-h-[44px]"
              />
            </div>
          )}

          {metric.metricType && (
            <div>
              <FieldLabel>
                <Trans>Context note</Trans>
              </FieldLabel>
              <Input
                value={metric.contextNote}
                onChange={(e) => updateMetric("contextNote", e.target.value)}
                placeholder={t`e.g. morning, before medication`}
                className="min-h-[44px]"
              />
            </div>
          )}
        </div>
      );

    default:
      return null;
  }
}

/* --- Main Modal --- */

export function JournalEntryModal({
  open,
  onOpenChange,
  entryType,
  editingEntry,
  onSaved,
  isPriority: isPriorityProp,
  planTier,
  activeMemberId,
  vaultMembers,
}: Props) {
  const isPriority = isPriorityProp ?? planTier === "priority";
  const { t } = useLingui();
  const isEditing = !!editingEntry;

  const [activeType, setActiveType] = useState(entryType);
  const [title, setTitle] = useState("");
  const [notes, setNotes] = useState("");
  const [eventDate, setEventDate] = useState("");
  const [tagsInput, setTagsInput] = useState("");
  const [tags, setTags] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [selectedMemberId, setSelectedMemberId] = useState<string | null>(null);

  // Smart reminder suggestion state
  const [suggestionState, setSuggestionState] = useState<{
    entryId: string;
    type: string;
  } | null>(null);
  const [creatingSuggestion, setCreatingSuggestion] = useState(false);

  // File upload fields
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>({});
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Reminder fields
  const [showReminder, setShowReminder] = useState(false);
  const [reminderDate, setReminderDate] = useState("");
  const [reminderMessage, setReminderMessage] = useState("");

  // Type-specific metadata state
  const [labName, setLabName] = useState("");
  const [labValues, setLabValues] = useState<LabValue[]>([emptyLabValue()]);
  const [medications, setMedications] = useState<Medication[]>([emptyMedication()]);
  const [prescriberName, setPrescriberName] = useState("");
  const [vaccine, setVaccine] = useState<VaccineData>(emptyVaccine());
  const [metric, setMetric] = useState<MetricData>(emptyMetric());

  const entryTypeLabels = useEntryTypeLabels();
  const titleLabel = useTypeTitleLabel(activeType, isEditing);
  const reminderSuggestionText = useReminderSuggestionText();

  function resetMetadataState() {
    setLabName("");
    setLabValues([emptyLabValue()]);
    setMedications([emptyMedication()]);
    setPrescriberName("");
    setVaccine(emptyVaccine());
    setMetric(emptyMetric());
  }

  // Reset form when opening
  useEffect(() => {
    if (open) {
      setSelectedFiles([]);
      setUploadProgress({});
      setUploading(false);
      setDragOver(false);
      setSuggestionState(null);
      setCreatingSuggestion(false);
      setSelectedMemberId(activeMemberId || null);
      setActiveType(editingEntry?.type || entryType);
      if (editingEntry) {
        setTitle(editingEntry.title);
        setNotes(editingEntry.notes || "");
        setEventDate(editingEntry.eventDate.slice(0, 10));
        setTags(editingEntry.tags);
        setTagsInput("");
        setShowReminder(editingEntry._count.reminders > 0);
        if (editingEntry.reminders[0]) {
          setReminderDate(editingEntry.reminders[0].remindAt.slice(0, 10));
          setReminderMessage(editingEntry.reminders[0].message);
        }

        // Restore type-specific metadata
        const meta = editingEntry.metadata as Record<string, unknown> | null;
        if (meta) {
          switch (editingEntry.type) {
            case "lab_result":
              setLabName(String(meta.labName || ""));
              setLabValues(parseLabValues(meta));
              break;
            case "prescription":
              setPrescriberName(String(meta.prescriberName || ""));
              setMedications(parseMedications(meta));
              break;
            case "vaccine":
              setVaccine(parseVaccine(meta));
              break;
            case "metric":
              setMetric(parseMetric(meta));
              break;
          }
        } else {
          resetMetadataState();
        }
      } else {
        setTitle("");
        setNotes("");
        setEventDate(new Date().toISOString().slice(0, 10));
        setTags([]);
        setTagsInput("");
        setShowReminder(activeType === "reminder");
        setReminderDate("");
        setReminderMessage("");
        resetMetadataState();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editingEntry, entryType]);

  const addTag = useCallback(() => {
    const tag = tagsInput.trim().toLowerCase();
    if (tag && !tags.includes(tag)) {
      setTags((prev) => [...prev, tag]);
    }
    setTagsInput("");
  }, [tagsInput, tags]);

  const removeTag = useCallback((tag: string) => {
    setTags((prev) => prev.filter((t) => t !== tag));
  }, []);

  const addFiles = useCallback((files: FileList | File[]) => {
    const valid = Array.from(files).filter((f) => f.size <= MAX_FILE_SIZE);
    setSelectedFiles((prev) => [...prev, ...valid]);
  }, []);

  const removeFile = useCallback((index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(false);
      if (e.dataTransfer.files.length > 0) addFiles(e.dataTransfer.files);
    },
    [addFiles],
  );

  const uploadFiles = useCallback(
    async (entryId: string) => {
      if (selectedFiles.length === 0) return;
      setUploading(true);
      for (const file of selectedFiles) {
        const formData = new FormData();
        formData.append("file", file);
        try {
          setUploadProgress((prev) => ({ ...prev, [file.name]: 0 }));
          const res = await apiFetch.raw(`/api/account/journal/${entryId}/attachments`, {
            method: "POST",
            body: formData,
          });
          setUploadProgress((prev) => ({ ...prev, [file.name]: res.ok ? 100 : -1 }));
          // Locked meanwhile: the rest would be refused too — the PIN gate comes back.
          if (await isVaultLockedResponse(res)) {
            reportVaultLocked();
            break;
          }
        } catch {
          setUploadProgress((prev) => ({ ...prev, [file.name]: -1 }));
        }
      }
      setUploading(false);
    },
    [selectedFiles],
  );

  const handleSave = useCallback(async () => {
    if (!title.trim() || !eventDate) return;
    setSaving(true);
    const toast = showToast(t`Saving…`, "loading");

    try {
      const metadata = buildMetadata(
        activeType,
        labName,
        labValues,
        medications,
        prescriberName,
        vaccine,
        metric,
      );

      const body: Record<string, unknown> = {
        type: activeType,
        title: title.trim(),
        notes: notes.trim() || null,
        eventDate: new Date(eventDate).toISOString(),
        tags,
        status: "final",
        metadata,
        vaultMemberId: selectedMemberId || undefined,
      };

      let savedEntryId: string;

      if (isEditing) {
        const data = await apiFetch.put<{ id: string }>(
          `/api/account/journal/${editingEntry!.id}`,
          body,
        );
        savedEntryId = data.id;
      } else {
        const data = await apiFetch.post<{ id: string }>("/api/account/journal", body);
        savedEntryId = data.id;
      }

      // Create reminder if set
      if (showReminder && reminderDate && reminderMessage.trim() && !isEditing) {
        await apiFetch.post(`/api/account/journal/${savedEntryId}/reminder`, {
          remindAt: new Date(reminderDate).toISOString(),
          message: reminderMessage.trim(),
        });
      }

      // Upload files after entry is saved
      if (selectedFiles.length > 0) {
        await uploadFiles(savedEntryId);
      }

      toast.update(t`Saved`, "success");

      // Show smart reminder suggestion for Priority users (non-edit, applicable types)
      if (isSuggestionType(activeType) && isPriority && !isEditing && !showReminder) {
        setSuggestionState({ entryId: savedEntryId, type: activeType });
      } else {
        onSaved();
      }
    } catch (err) {
      if (handleVaultLocked(err)) {
        toast.update(t`Unlock your medical vault to continue.`, "info");
      } else {
        toast.update(t`Could not save. Try again.`, "error");
      }
    } finally {
      setSaving(false);
    }
  }, [
    title,
    notes,
    eventDate,
    tags,
    activeType,
    isEditing,
    editingEntry,
    showReminder,
    reminderDate,
    reminderMessage,
    selectedFiles,
    uploadFiles,
    onSaved,
    isPriority,
    selectedMemberId,
    labName,
    labValues,
    medications,
    prescriberName,
    vaccine,
    metric,
    t,
  ]);

  const handleSuggestionReminder = useCallback(
    async (months: number) => {
      if (!suggestionState) return;
      setCreatingSuggestion(true);
      try {
        const remindAt = addMonths(new Date(), months);
        await apiFetch.post(`/api/account/journal/${suggestionState.entryId}/reminder`, {
          remindAt: remindAt.toISOString(),
          message: title.trim(),
        });
      } catch (err) {
        // Locked meanwhile: the PIN gate comes back. Otherwise silent.
        handleVaultLocked(err);
      } finally {
        setCreatingSuggestion(false);
        setSuggestionState(null);
        onSaved();
      }
    },
    [suggestionState, title, onSaved],
  );

  const handleSkipSuggestion = useCallback(() => {
    setSuggestionState(null);
    onSaved();
  }, [onSaved]);

  const titleNode = (() => {
    const member = vaultMembers?.find((m) => m.id === selectedMemberId);
    if (member && member.relationship !== "self") {
      return `${titleLabel} — ${member.name}`;
    }
    return titleLabel;
  })();

  // Footer for the form view (omitted while showing suggestion).
  const footer = suggestionState ? null : (
    <div className="flex gap-2">
      <Button variant="outline" className="min-h-[44px] flex-1" onClick={() => onOpenChange(false)}>
        <Trans>Cancel</Trans>
      </Button>
      <Button
        className="min-h-[44px] flex-1"
        disabled={saving || uploading || !title.trim() || !eventDate}
        onClick={handleSave}
      >
        {saving || uploading ? (
          <>
            <Loader2 className="mr-1.5 size-4 animate-spin" />
            {uploading ? <Trans>Uploading…</Trans> : <Trans>Saving…</Trans>}
          </>
        ) : (
          <Trans>Save</Trans>
        )}
      </Button>
    </div>
  );

  return (
    <ResponsiveSheet
      open={open}
      onClose={() => onOpenChange(false)}
      title={titleNode}
      width="lg"
      ariaLabel={typeof titleNode === "string" ? titleNode : t`Journal entry`}
      footer={footer}
    >
      <AnimatePresence mode="wait">
        {suggestionState ? (
          <m.div
            key="suggestion"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
            className="bg-primary/5 border-primary/20 rounded-xl border p-4"
          >
            <div className="flex items-start gap-3">
              <div className="bg-primary/10 flex size-9 shrink-0 items-center justify-center rounded-lg">
                <Sparkles className="text-primary size-5" />
              </div>
              <div className="flex-1">
                <p className="text-foreground text-sm font-semibold">
                  {reminderSuggestionText(suggestionState.type)}
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="min-h-[40px] text-xs"
                    disabled={creatingSuggestion}
                    onClick={() => handleSuggestionReminder(3)}
                  >
                    {creatingSuggestion ? <Loader2 className="mr-1 size-3 animate-spin" /> : null}
                    <Trans>In 3 months</Trans>
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="min-h-[40px] text-xs"
                    disabled={creatingSuggestion}
                    onClick={() => handleSuggestionReminder(6)}
                  >
                    <Trans>In 6 months</Trans>
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="min-h-[40px] text-xs"
                    disabled={creatingSuggestion}
                    onClick={() => handleSuggestionReminder(12)}
                  >
                    <Trans>In 12 months</Trans>
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-muted-foreground min-h-[40px] text-xs"
                    disabled={creatingSuggestion}
                    onClick={handleSkipSuggestion}
                  >
                    <Trans>Skip</Trans>
                  </Button>
                </div>
              </div>
            </div>
          </m.div>
        ) : (
          <m.div key="form" className="space-y-4">
            {/* Entry type selector */}
            <div>
              <label className="text-foreground mb-1.5 flex items-center gap-1.5 text-sm font-medium">
                <Trans>Entry type</Trans>
              </label>
              <select
                value={activeType}
                onChange={(e) => setActiveType(e.target.value)}
                className="border-border bg-background text-foreground min-h-[44px] w-full rounded-lg border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-teal-500/20"
              >
                {ALL_ENTRY_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {entryTypeLabels[type]}
                  </option>
                ))}
              </select>
            </div>

            {/* Title */}
            <div>
              <label className="text-foreground mb-1.5 block text-sm font-medium">
                <Trans>Title</Trans>
              </label>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={t`Short title (e.g. annual checkup)`}
                className="min-h-[44px]"
              />
            </div>

            {/* Family member selector */}
            {vaultMembers && vaultMembers.length > 1 && (
              <div>
                <label className="text-foreground mb-1.5 flex items-center gap-1.5 text-sm font-medium">
                  <Users className="size-3.5" />
                  <Trans>For family member</Trans>
                </label>
                <select
                  value={selectedMemberId || ""}
                  onChange={(e) => setSelectedMemberId(e.target.value || null)}
                  className="border-border bg-background text-foreground min-h-[44px] w-full rounded-lg border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-teal-500/20"
                >
                  <option value="">{t`Myself`}</option>
                  {vaultMembers
                    .filter((m) => m.relationship !== "self")
                    .map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.name} ({m.relationship})
                      </option>
                    ))}
                </select>
              </div>
            )}

            {/* Date */}
            <div>
              <label className="text-foreground mb-1.5 flex items-center gap-1.5 text-sm font-medium">
                <Calendar className="size-3.5" />
                <Trans>Date</Trans>
              </label>
              <Input
                type="date"
                value={eventDate}
                onChange={(e) => setEventDate(e.target.value)}
                className="min-h-[44px]"
              />
            </div>

            {/* Notes */}
            <div>
              <label className="text-foreground mb-1.5 block text-sm font-medium">
                <Trans>Notes</Trans>
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder={t`Additional details, symptoms, observations…`}
                rows={3}
                className="border-border bg-background text-foreground placeholder:text-muted-foreground w-full rounded-lg border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-teal-500/20"
              />
            </div>

            {/* File upload — available on all entry types */}
            <div>
              <label className="text-foreground mb-1.5 flex items-center gap-1.5 text-sm font-medium">
                <Paperclip className="size-3.5" />
                <Trans>Attachments</Trans>
              </label>
              <div
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragOver(true);
                }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                className={cn(
                  "flex min-h-[80px] cursor-pointer flex-col items-center justify-center gap-1.5 rounded-lg border-2 border-dashed p-4 transition-colors",
                  dragOver
                    ? "border-primary bg-primary/5"
                    : "border-border hover:border-primary/50 hover:bg-muted/50",
                )}
              >
                <Upload className="text-muted-foreground size-5" />
                <p className="text-muted-foreground text-center text-xs">
                  <Trans>Drop files here or tap to browse</Trans>
                </p>
                <p className="text-muted-foreground/60 text-center text-[10px]">
                  PDF, JPG, PNG, WebP, DICOM &middot; <Trans>Max 10 MB per file</Trans>
                </p>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPTED_FILE_TYPES}
                multiple
                className="hidden"
                onChange={(e) => {
                  if (e.target.files) addFiles(e.target.files);
                  e.target.value = "";
                }}
              />
              {selectedFiles.length > 0 && (
                <div className="mt-2 space-y-1.5">
                  {selectedFiles.map((file, i) => {
                    const progress = uploadProgress[file.name];
                    const isImage = file.type.startsWith("image/");
                    return (
                      <div
                        key={`${file.name}-${i}`}
                        className="bg-muted/50 flex items-center gap-2 rounded-lg px-3 py-2"
                      >
                        {isImage ? (
                          <ImageIcon className="text-muted-foreground size-4 shrink-0" />
                        ) : (
                          <FileText className="text-muted-foreground size-4 shrink-0" />
                        )}
                        <div className="min-w-0 flex-1">
                          <p className="text-foreground truncate text-xs font-medium">
                            {file.name}
                          </p>
                          <p className="text-muted-foreground text-[10px]">
                            {formatFileSize(file.size)}
                          </p>
                          {progress !== undefined && progress >= 0 && progress < 100 && (
                            <div className="bg-muted mt-1 h-1 overflow-hidden rounded-full">
                              <div
                                className="bg-primary h-full transition-[width] duration-200"
                                style={{ width: `${progress}%` }}
                              />
                            </div>
                          )}
                          {progress === -1 && (
                            <p className="text-destructive mt-0.5 text-[10px]">
                              <Trans>Upload failed</Trans>
                            </p>
                          )}
                        </div>
                        {!uploading && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              removeFile(i);
                            }}
                            aria-label={t`Remove file`}
                            className="text-muted-foreground hover:text-destructive flex size-7 shrink-0 items-center justify-center rounded"
                          >
                            <X className="size-3.5" />
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Tags */}
            <div>
              <label className="text-foreground mb-1.5 flex items-center gap-1.5 text-sm font-medium">
                <Tag className="size-3.5" />
                <Trans>Tags</Trans>
              </label>
              <div className="flex gap-2">
                <Input
                  value={tagsInput}
                  onChange={(e) => setTagsInput(e.target.value)}
                  placeholder={t`Add tag and press Enter`}
                  className="min-h-[44px] flex-1"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addTag();
                    }
                  }}
                />
                <Button
                  variant="outline"
                  size="sm"
                  className="min-h-[44px]"
                  onClick={addTag}
                  disabled={!tagsInput.trim()}
                  aria-label={t`Add tag`}
                >
                  +
                </Button>
              </div>
              {tags.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {tags.map((tag) => (
                    <span
                      key={tag}
                      className="bg-muted text-foreground inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs"
                    >
                      {tag}
                      <button
                        type="button"
                        onClick={() => removeTag(tag)}
                        aria-label={t`Remove tag`}
                        className="hover:text-destructive"
                      >
                        <X className="size-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Type-specific metadata fields */}
            <TypeSpecificFields
              entryType={activeType}
              labName={labName}
              setLabName={setLabName}
              labValues={labValues}
              setLabValues={setLabValues}
              medications={medications}
              setMedications={setMedications}
              prescriberName={prescriberName}
              setPrescriberName={setPrescriberName}
              vaccine={vaccine}
              setVaccine={setVaccine}
              metric={metric}
              setMetric={setMetric}
            />

            {/* Reminder toggle */}
            <div className="border-border rounded-lg border p-3">
              <button
                type="button"
                onClick={() => setShowReminder((s) => !s)}
                className="flex min-h-[36px] w-full items-center gap-2 text-sm font-medium"
              >
                <Bell
                  className={cn("size-4", showReminder ? "text-primary" : "text-muted-foreground")}
                />
                <span className="text-foreground flex-1 text-left">
                  <Trans>Add reminder</Trans>
                </span>
                <span
                  className={cn(
                    "rounded-full px-2 py-0.5 text-xs",
                    showReminder ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground",
                  )}
                >
                  {showReminder ? t`On` : t`Off`}
                </span>
              </button>

              {showReminder && (
                <div className="border-border mt-3 space-y-3 border-t pt-3">
                  <div>
                    <label className="text-foreground mb-1.5 block text-xs font-medium">
                      <Trans>Reminder date</Trans>
                    </label>
                    <Input
                      type="date"
                      value={reminderDate}
                      onChange={(e) => setReminderDate(e.target.value)}
                      className="min-h-[44px]"
                    />
                  </div>
                  <div>
                    <label className="text-foreground mb-1.5 block text-xs font-medium">
                      <Trans>Message</Trans>
                    </label>
                    <Input
                      value={reminderMessage}
                      onChange={(e) => setReminderMessage(e.target.value)}
                      placeholder={t`What should we remind you about?`}
                      className="min-h-[44px]"
                    />
                  </div>
                </div>
              )}
            </div>
          </m.div>
        )}
      </AnimatePresence>
    </ResponsiveSheet>
  );
}
